import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { Campaign, EventAvailable, Money, AssignmentTurnedIn } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../../../shared/api';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { formatINR } from '../../../shared/utils';

export default function StudentDashboard() {
  const user = useAuthStore((s) => s.user);
  const { data } = useQuery({ queryKey: ['student-dashboard'], queryFn: async () => (await api.get('/dashboard/student')).data });

  return (
    <Box>
      <PageHeader title={`Welcome, ${user?.fullName.split(' ')[0]} 👋`} subtitle="Your latest at a glance." />
      <Grid container spacing={2}>
        <StatCard color="success.main" icon={<EventAvailable />} label="Attendance" value={`${data?.attendancePercent ?? 0}%`} link="/portal/attendance" sub={data?.attendancePercent < 75 ? 'Below 75%' : 'Good standing'} />
        <StatCard color="primary.main" icon={<AssignmentTurnedIn />} label="Latest result" value={data?.latestResult ? `${data.latestResult.percent}%` : '—'} link="/portal/marks" sub={data?.latestResult?.examName ?? 'No published result'} />
        <StatCard color="error.main" icon={<Money />} label="Fee balance" value={formatINR(data?.fee?.balance ?? 0)} link="/portal/fees" sub={`of ${formatINR(data?.fee?.totalDue ?? 0)} due`} />
        <StatCard color="secondary.main" icon={<Campaign />} label="Notices" value={`${data?.noticesCount ?? 0}`} link="/portal/notices" sub="Available" />

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Today's timetable</Typography>
              {(data?.todayTimetable ?? []).length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No classes today.</Typography>
              ) : (
                (data?.todayTimetable ?? []).map((e: any) => (
                  <Stack key={e.id} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: '1px solid #E2E8F0' }}>
                    <Typography>P{e.period} · {e.startTime}–{e.endTime}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>Subject {e.subjectId.replace('subj_', '').toUpperCase()}</Typography>
                  </Stack>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Upcoming assessments</Typography>
              {(data?.upcomingAssessments ?? []).length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>None pending.</Typography>
              ) : (
                (data?.upcomingAssessments ?? []).map((a: any) => (
                  <Stack key={a.id} sx={{ py: 1, borderBottom: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontWeight: 600 }}>{a.title}</Typography>
                    <Typography variant="caption" color="text.secondary">Due {a.dueDate}</Typography>
                  </Stack>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function StatCard({ color, icon, label, value, sub, link }: any) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card component={RouterLink} to={link} sx={{ textDecoration: 'none', display: 'block', '&:hover': { borderColor: 'primary.main' } }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box sx={{ color, bgcolor: `${color}10`, p: 1, borderRadius: 2 }}>{icon}</Box>
            <Typography variant="overline" color="text.secondary">{label}</Typography>
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{sub}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}
